import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { EmailField } from "@RHCommerceDev/component-standard-fields";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  emailAtom,
  isTokenExpirationError,
  useSetAuthenticationDialogCurrentScreenAtom
} from "@RHCommerceDev/hooks/atoms";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import { getGlobal } from "@RHCommerceDev/utils/Globals/getGlobal";
import { useStytch } from "@stytch/react";
import { useSignInTemplate } from "@RHCommerceDev/hooks/useSignInTemplate";
import { useAtomValue, useSetAtom } from "jotai";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import memoize from "utils/memoize";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import analyticsLoader from "analytics/loader";
import { emailRegex } from "utils/regex";
import { getClientOrigin } from "@RHCommerceDev/utils/getClientOrigin";
import { getLocaleFromPath } from "@RHCommerceDev/utils/intlUtils";
const env = getGlobal("_env");

const SignInWithASecureLink = () => {
  const form = useForm({
    mode: "onBlur",
    defaultValues: {
      email: ""
    }
  });
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const templateId = useSignInTemplate();
  const setEmailValue = useSetAtom(emailAtom);
  const { location } = useHistory();
  const { locale } = getLocaleFromPath(location?.pathname) || {
    locale: "/us/en/"
  };

  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const isTokenExpirationErrors = useAtomValue(isTokenExpirationError);

  const stytchClient = useStytch();
  const hostName = processEnvServer
    ? getClientOrigin()
    : window?.location?.origin;

  const { run, loading } = useAsync(async ({ email }) => {
    try {
      setEmailValue(email);
      // if the user already has an account only then the user will get mail
      await stytchClient.magicLinks.email.send(email, {
        login_magic_link_url: `${hostName}${locale}?redirect_url=${location.pathname}?type=login`,
        login_expiration_minutes: 15,
        login_template_id: templateId
      });

      setAuthenticationDialogCurrentScreen("CheckYourInbox");
      if (!processEnvServer) {
        analyticsLoader(a =>
          a.emitAnalyticsEvent(
            document.querySelector("#spa-root > *")! as HTMLElement,
            a.EVENTS.LOGIN.INT_TYPE,
            {
              loggedIn: "Success",
              login_method: "Secure Link Email"
            }
          )
        );
      }
    } catch (error) {
      console.log(error);
      if (!processEnvServer) {
        analyticsLoader(a =>
          a.emitAnalyticsEvent(
            document.querySelector("#spa-root > *")! as HTMLElement,
            a.EVENTS.LOGIN.INT_TYPE,
            {
              loggedIn: "Failure",
              login_method: "Secure Link Email"
            }
          )
        );
      }
    }
  });

  const onSubmit = React.useCallback(
    values => {
      return run(values);
    },
    [run]
  );
  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  return (
    <div className="flex w-max flex-col text-center">
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Typography className="text-transform: font-primary-rh-thin text-xl uppercase">
            {stytchData?.SIGN_IN_WITH}
          </Typography>
          {isTokenExpirationErrors === true && (
            <Typography className="mt-6 text-center font-primary-rhlight">
              {stytchData?.Your_secure_link}
            </Typography>
          )}
        </div>
      </div>

      <Typography
        className={`font-primary-rhlight  ${
          isTokenExpirationErrors === true
            ? "mb-6 customXs:mb-4"
            : "my-6 customXs:my-4"
        } `}
      >
        {!!isTokenExpirationErrors
          ? stytchData?.Please_re_enter_your
          : stytchData?.Enter_your_Email}
      </Typography>

      <FormProvider {...form}>
        <form
          className="flex w-80 flex-col gap-10"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="h-16 w-[311px]">
            <EmailField
              label="Email"
              className="[&>p]:text-xxsm [&>p]:!text-rh-red"
              rules={{
                required: stytchData?.Email_address_is,
                pattern: {
                  value: emailRegex,
                  message: stytchData?.Stytch_Error_Object?.invalid_email
                }
              }}
            />
          </div>

          <Button
            type="submit"
            variant="secondary"
            disabled={loading || !isFormComplete}
            className="mt-0 h-[48px] w-[311px]"
          >
            {stytchData?.CONTINUE}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
};

export default memoize(SignInWithASecureLink);
